import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert, 
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import CameraModal from '../../components/camera/CameraModal';
import { useMaintenanceAI } from '../../hooks/useMaintenanceAI';
import AsyncMaintenanceForm from '../../components/maintenance/AsyncMaintenanceForm';
import { getSuppliers, createSupplier } from '../../services/supplierService';
import vehicleService from '../../services/vehicleService';
import { organizationService } from '../../services/organizationService';
import { createMaintenanceRecord } from '../../services/maintenanceService';
import { ExtractedMaintenanceData } from '../../services/aiService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { Vehicle } from '../../types/vehicle';
import { Supplier } from '../../types/supplier';
import { MaintenanceRecord, MaintenanceType, MaintenanceDocument } from '../../types/maintenance';

const findOrCreateServiceProvider = async (
  serviceProvider: ExtractedMaintenanceData['serviceProvider'],
  organizationId: string,
  existingProviders: (Supplier & { id: string })[]
): Promise<string | undefined> => {
  if (!serviceProvider?.name) return undefined;

  // Rechercher le prestataire existant
  const matchedProvider = existingProviders.find(p => {
    const nameMatch = p.name.toLowerCase() === serviceProvider.name?.toLowerCase();
    const addressMatch = serviceProvider.address 
      ? p.address.toLowerCase().includes(serviceProvider.address.toLowerCase())
      : false;
    return nameMatch || (nameMatch && addressMatch);
  });

  if (matchedProvider?.id) {
    return matchedProvider.id;
  }

  // Créer un nouveau prestataire si l'adresse est disponible
  if (serviceProvider.address) {
    try {
      const newProvider: Omit<Supplier, 'id' | 'organizationId'> = {
        name: serviceProvider.name,
        type: 'garage',
        address: serviceProvider.address,
        contactName: '',
        contactPhone: serviceProvider.phone || '',
        email: serviceProvider.email || '',
        services: ['maintenance'],
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const newProviderId = await createSupplier(organizationId, newProvider);
      return newProviderId;
    } catch (error) {
      console.error('Error creating new supplier:', error);
      return undefined;
    }
  }

  return undefined;
};

export const QuickMaintenanceEntryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, currentOrganization } = useAuth();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [vehicles, setVehicles] = useState<{ id: string; name: string }[]>([]);
  const [serviceProviders, setServiceProviders] = useState<(Supplier & { id: string })[]>([]);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const { processMultipleImages, isProcessing, error, setError } = useMaintenanceAI();
  const [extractedData, setExtractedData] = useState<ExtractedMaintenanceData | null>(null);
  const [processingStep, setProcessingStep] = useState<'capture' | 'processing' | 'form'>('capture');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!currentUser || !currentOrganization) {
      navigate('/');
      return;
    }

    const loadData = async () => {
      try {
        console.log('Starting data load with:', {
          user: currentUser?.id,
          organization: currentOrganization?.id
        });
        
        setIsLoading(true);
        setDataLoadError(null);
        const organizationId = currentOrganization?.id;
        if (!organizationId) {
          console.error('Organization ID missing during data load');
          throw new Error('Organization ID is required');
        }
        console.log('Loading data for organization:', organizationId);

        // Check if user is Admin
        const adminStatus = await organizationService.isUserAdmin(organizationId, currentUser.id);
        setIsAdmin(adminStatus);

        // Load vehicles and providers
        let vehiclesData: Vehicle[];

        if (adminStatus) {
          // Admin: load all organization vehicles
          const [allVehicles, providersData] = await Promise.all([
            vehicleService.getVehiclesByOrganization(organizationId),
            getSuppliers(organizationId)
          ]).catch(error => {
            console.error('Error in Promise.all:', error);
            throw error;
          });

          vehiclesData = allVehicles;

          console.log('Loaded vehicles (Admin):', vehiclesData);
          console.log('Loaded providers:', providersData);

          const filteredProviders = providersData
            .filter((p: Supplier): p is Supplier & { id: string } => {
              if (!p.id) console.warn('Provider without ID:', p);
              return !!p.id;
            });

          setServiceProviders(filteredProviders);
        } else {
          // User: load only assigned vehicle
          const [assignedVehicle, providersData] = await Promise.all([
            vehicleService.getAssignedVehicle(organizationId, currentUser.id),
            getSuppliers(organizationId)
          ]).catch(error => {
            console.error('Error in Promise.all:', error);
            throw error;
          });

          if (!assignedVehicle) {
            throw new Error(t('vehicle.errors.noVehicleAssigned'));
          }

          vehiclesData = [assignedVehicle];

          console.log('Loaded assigned vehicle (User):', vehiclesData);
          console.log('Loaded providers:', providersData);

          const filteredProviders = providersData
            .filter((p: Supplier): p is Supplier & { id: string } => {
              if (!p.id) console.warn('Provider without ID:', p);
              return !!p.id;
            });

          setServiceProviders(filteredProviders);
        }

        const filteredVehicles = vehiclesData
          .filter((v: Vehicle): v is Vehicle & { id: string } => {
            if (!v.id) console.warn('Vehicle without ID:', v);
            return !!v.id;
          })
          .map((v) => ({
            id: v.id,
            name: `${v.make} ${v.model} (${v.licensePlate})`
          }));

        console.log('Filtered vehicles:', filteredVehicles);

        setVehicles(filteredVehicles);
      } catch (error) {
        console.error('Error loading data:', error);
        setDataLoadError(t('maintenance.errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentUser, currentOrganization, navigate, t]);

  const compressImage = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            // Calculate new dimensions while maintaining aspect ratio
            let width = img.width;
            let height = img.height;
            const maxDimension = 1600;

            if (width > height && width > maxDimension) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else if (height > maxDimension) {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }

            // Ensure dimensions are integers
            width = Math.floor(width);
            height = Math.floor(height);

            canvas.width = width;
            canvas.height = height;

            // Clear canvas and draw image
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            // Get compressed base64
            const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
            resolve(compressedBase64.split(',')[1]);
          } catch (error) {
            console.error('Error compressing image:', error);
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const handleCapture = useCallback(async (file: File) => {
    try {
      const compressedBase64 = await compressImage(file);
      setCapturedImages(prev => [...prev, compressedBase64]);
    } catch (error) {
      console.error('Error handling capture:', error);
      setError(t('maintenance.quickEntry.errors.captureError'));
    }
  }, [compressImage, t]);

  const handleCameraClose = useCallback(() => {
    setIsCameraOpen(false);
  }, []);

  const handleProcessImages = useCallback(async () => {
    try {
      console.log('Starting image processing...');
      setIsUploadComplete(true);
      setProcessingStep('processing');
      
      console.log('Processing images:', capturedImages.length);
      const data = await processMultipleImages(capturedImages);
      console.log('Received processed data:', data);
      
      if (data) {
        console.log('Creating service provider if needed...');
        // Créer le prestataire avant de passer à l'étape du formulaire
        let updatedData = { ...data };
        if (data.serviceProvider?.name && currentOrganization?.id) {
          const providerId = await createServiceProviderIfNeeded(data);
          if (providerId) {
            updatedData = { ...updatedData, serviceProviderId: providerId };
          }
        }
        
        console.log('Setting extracted data and updating step to form');
        setExtractedData(updatedData);
        setProcessingStep('form');
      } else {
        console.error('No data received from image processing');
        setProcessingStep('capture');
        setError(t('maintenance.quickEntry.errors.extractionFailed'));
      }
    } catch (error) {
      console.error('Error processing images:', error);
      setProcessingStep('capture');
      setError(t('maintenance.quickEntry.errors.processingError'));
    }
  }, [capturedImages, processMultipleImages, t]);

  const handleRetry = useCallback(() => {
    setCapturedImages([]);
    setExtractedData(null);
    setProcessingStep('capture');
    setSubmitError(null);
    setIsUploadComplete(false);
  }, []);

  const generateStructuredDescription = (data: ExtractedMaintenanceData): string => {
    const sections: string[] = [];

    // Informations de base
    sections.push(`Type d'intervention : ${t(`maintenance.types.${data.type || 'repair'}`)}`);
    
    // Pièces détachées
    if (data.detailedOperations?.parts && data.detailedOperations.parts.length > 0) {
      sections.push('\nPièces détachées :');
      data.detailedOperations.parts.forEach(part => {
        const quantity = part.quantity ? ` (${part.quantity})` : '';
        const price = part.unitPrice ? ` - ${part.unitPrice.toFixed(2)} €` : '';
        sections.push(`- ${part.name}${quantity}${price}`);
      });
    }

    // Main d'œuvre
    if (data.detailedOperations?.labor && data.detailedOperations.labor.length > 0) {
      sections.push('\nOpérations de main d\'œuvre :');
      data.detailedOperations.labor.forEach(operation => {
        const hours = operation.hours ? ` (${operation.hours}h)` : '';
        const cost = operation.cost ? ` - ${operation.cost.toFixed(2)} €` : '';
        sections.push(`- ${operation.description}${hours}${cost}`);
      });
    }

    // Informations véhicule
    if (data.vehicleInfo) {
      const vehicleInfos: string[] = [];
      if (data.vehicleInfo.make) vehicleInfos.push(data.vehicleInfo.make);
      if (data.vehicleInfo.model) vehicleInfos.push(data.vehicleInfo.model);
      if (data.vehicleInfo.licensePlate) vehicleInfos.push(`Immat: ${data.vehicleInfo.licensePlate}`);
      if (data.vehicleInfo.mileage) vehicleInfos.push(`${data.vehicleInfo.mileage} km`);
      
      if (vehicleInfos.length > 0) {
        sections.push(`\nVéhicule : ${vehicleInfos.join(' - ')}`);
      }
    }

    // Description originale si présente
    if (data.description) {
      sections.push(`\nNotes supplémentaires :\n${data.description}`);
    }

    return sections.join('\n');
  };

  const validateRequiredData = () => {
    console.log('Validating required data with:', {
      currentUser,
      currentOrganization,
      vehicles
    });

    if (!currentOrganization?.id || !currentUser?.id || vehicles.length === 0) {
      const missingData = [];
      if (!currentOrganization?.id) {
        console.error('Missing organization ID:', currentOrganization);
        missingData.push('organization');
      }
      if (!currentUser?.id) {
        console.error('Missing user ID:', currentUser);
        missingData.push('user');
      }
      if (vehicles.length === 0) {
        console.error('No vehicles available');
        missingData.push('vehicles');
      }
      
      const error = `Missing required data: ${missingData.join(', ')}`;
      console.error('Validation failed:', {
        organization: currentOrganization?.id,
        user: currentUser?.id,
        vehiclesCount: vehicles.length,
        missingData
      });
      throw new Error(error);
    }

    const validatedData = {
      organizationId: currentOrganization.id,
      userId: currentUser.id,
      vehicleId: vehicles[0].id
    };
    console.log('Data validation successful:', validatedData);
    return validatedData;
  };

  const createServiceProviderIfNeeded = async (data: ExtractedMaintenanceData) => {
    if (!data.serviceProvider?.name || !currentOrganization?.id) return undefined;

    // Rechercher le prestataire existant
    const matchedProvider = serviceProviders.find(p => {
      const nameMatch = p.name.toLowerCase() === data.serviceProvider?.name?.toLowerCase();
      const addressMatch = data.serviceProvider?.address 
        ? p.address.toLowerCase().includes(data.serviceProvider.address.toLowerCase())
        : false;
      return nameMatch || (nameMatch && addressMatch);
    });

    if (matchedProvider?.id) {
      return matchedProvider.id;
    }

    // Créer un nouveau prestataire si l'adresse est disponible
    if (data.serviceProvider.address) {
      try {
        const newProvider: Omit<Supplier, 'id' | 'organizationId'> = {
          name: data.serviceProvider.name,
          type: 'garage',
          address: data.serviceProvider.address,
          contactName: '',
          contactPhone: data.serviceProvider.phone || '',
          email: data.serviceProvider.email || '',
          services: ['maintenance'],
          createdAt: new Date(),
          updatedAt: new Date()
        };

        const newProviderId = await createSupplier(currentOrganization.id, newProvider);
        // Mettre à jour la liste des prestataires
        if (newProviderId) {
          setServiceProviders(prev => [...prev, { 
            ...newProvider, 
            id: newProviderId,
            organizationId: currentOrganization.id
          }]);
        }
        return newProviderId;
      } catch (error) {
        console.error('Error creating new supplier:', error);
        return undefined;
      }
    }

    return undefined;
  };

  const prepareMaintenanceData = async (data: ExtractedMaintenanceData): Promise<Omit<MaintenanceRecord, 'id'>> => {
    if (!currentUser || !currentOrganization) {
      throw new Error('User must be authenticated');
    }

    console.log('Preparing maintenance data with:', {
      organization: currentOrganization.id,
      user: currentUser.id,
      vehiclesCount: vehicles.length,
      extractedData: data
    });

    const { organizationId, userId, vehicleId } = validateRequiredData();
    // Utiliser la date de la facture ou la date actuelle comme fallback
    const invoiceDate = data.date ? Timestamp.fromDate(data.date) : Timestamp.now();

    const documents: MaintenanceDocument[] = capturedImages.map((img, index) => ({
      id: `temp-${index}`,
      type: 'invoice',
      url: `data:image/jpeg;base64,${img}`,
      name: `invoice-${index + 1}.jpg`,
      uploadedAt: invoiceDate,
      uploadedBy: userId
    }));

    // Validate maintenance type
    const maintenanceType = (data.type || 'repair') as MaintenanceType;
    if (!['revision', 'technical_control', 'repair'].includes(maintenanceType)) {
      throw new Error('Invalid maintenance type');
    }

    const maintenanceData: Omit<MaintenanceRecord, 'id'> = {
      organizationId,
      vehicleId: vehicles[0].id, // Use the selected vehicle
      openDate: invoiceDate,
      closeDate: invoiceDate,
      status: 'completed',
      type: maintenanceType,
      description: generateStructuredDescription(data),
      costHT: typeof data.costHT === 'number' ? data.costHT : 0,
      costTTC: typeof data.costTTC === 'number' ? data.costTTC : 0,
      events: [],
      documents,
      detailedOperations: data.detailedOperations ? {
        parts: data.detailedOperations.parts?.filter(p => p.name) || [],
        labor: data.detailedOperations.labor?.filter(l => l.description) || []
      } : undefined,
      serviceProviderId: data.serviceProviderId,
      createdBy: userId,
      updatedBy: userId,
      createdAt: invoiceDate,
      updatedAt: invoiceDate
    };

    console.log('Prepared maintenance data:', maintenanceData);
    return maintenanceData;
  };

  const handleSubmit = async (formData: Partial<MaintenanceRecord>) => {
    if (!extractedData) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Préparer les données de base
      const baseData = await prepareMaintenanceData(extractedData);

      // Ensure all required fields are properly typed and present
      const maintenanceData: Omit<MaintenanceRecord, 'id'> = {
        organizationId: baseData.organizationId,
        vehicleId: formData.vehicleId || baseData.vehicleId,
        openDate: formData.openDate || baseData.openDate,
        closeDate: formData.status === 'completed' ? (formData.closeDate || Timestamp.now()) : undefined,
        status: formData.status || baseData.status,
        type: formData.type || baseData.type,
        description: formData.description || baseData.description,
        costHT: typeof formData.costHT === 'number' ? formData.costHT : baseData.costHT,
        costTTC: typeof formData.costTTC === 'number' ? formData.costTTC : baseData.costTTC,
        events: baseData.events || [],
        documents: baseData.documents || [],
        serviceProviderId: formData.serviceProviderId || baseData.serviceProviderId,
        detailedOperations: formData.detailedOperations || baseData.detailedOperations,
        createdBy: baseData.createdBy,
        updatedBy: baseData.updatedBy,
        createdAt: baseData.createdAt,
        updatedAt: Timestamp.now()
      };

      // Vérifier que tous les champs requis sont présents et valides
      if (!maintenanceData.organizationId || !maintenanceData.vehicleId) {
        throw new Error('Missing required fields');
      }

      console.log('Submitting maintenance data:', maintenanceData);
      await createMaintenanceRecord(maintenanceData);
      navigate('/maintenance');
    } catch (error) {
      console.error('Error submitting maintenance record:', error);
      setSubmitError(t('maintenance.quickEntry.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (dataLoadError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {dataLoadError}
        </Alert>
      </Box>
    );
  }

  if (!currentUser || !currentOrganization) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {t('common.errors.unauthorized')}
        </Alert>
      </Box>
    );
  }

  if (vehicles.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {t('maintenance.errors.noVehicles')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('maintenance.quickEntry.title')}
        </Typography>

        {processingStep === 'capture' && (
          <Stack spacing={2} alignItems="center">
            <Typography>
              {t('maintenance.quickEntry.instructions')}
            </Typography>

            {/* Sélecteur de véhicule */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('maintenance.form.fields.vehicle')}</InputLabel>
              <Select
                value={vehicles[0]?.id || ''}
                onChange={(e: SelectChangeEvent<string>) => {
                  const selectedVehicle = vehicles.find(v => v.id === e.target.value);
                  if (selectedVehicle) {
                    setVehicles([selectedVehicle, ...vehicles.filter(v => v.id !== selectedVehicle.id)]);
                  }
                }}
                label={t('maintenance.form.fields.vehicle')}
                required
              >
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {vehicles[0]?.id ? (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsCameraOpen(true)}
                  disabled={isUploadComplete}
                >
                  {t('common.actionTypes.takePhoto')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  component="label"
                  disabled={isUploadComplete}
                >
                  {t('common.actionTypes.uploadFiles')}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => handleCapture(file));
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Stack>
            ) : (
              <Alert severity="info">
                {t('maintenance.errors.selectVehicle')}
              </Alert>
            )}

            {capturedImages.length > 0 && (
              <Stack spacing={2} alignItems="center">
                <Typography>
                  {t('maintenance.quickEntry.photosCaptured', { count: capturedImages.length })}
                </Typography>
                {!isUploadComplete && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleProcessImages}
                  >
                    {t('maintenance.quickEntry.confirmUpload')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {processingStep === 'processing' && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography ml={2}>
              {t('maintenance.quickEntry.processing')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}

        {processingStep === 'form' && extractedData && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('maintenance.quickEntry.dataExtracted')}
            </Alert>
            {currentUser && currentOrganization && vehicles.length > 0 ? (
              <AsyncMaintenanceForm
                extractedData={extractedData}
                prepareData={prepareMaintenanceData}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/maintenance')}
                vehicles={vehicles}
                serviceProviders={serviceProviders.map(p => ({ id: p.id, name: p.name }))}
                preSelectedVehicleId={vehicles[0]?.id}
              />
            ) : (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t('maintenance.errors.missingData')}
              </Alert>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleRetry}
              sx={{ mt: 2 }}
              disabled={isSubmitting}
            >
              {t('common.actionTypes.retry')}
            </Button>
          </>
        )}

        <CameraModal
          open={isCameraOpen}
          onClose={handleCameraClose}
          onPhotoTaken={handleCapture}
        />
      </Paper>
    </Box>
  );
};
